import { Component } from '@theme/component';
import { WishlistUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {Object} WishlistRefs
 * @property {HTMLElement} icon - The wishlist icon element
 * @property {HTMLElement} [notification] - Optional notification element
 * @property {HTMLElement} [counter] - Optional counter element
 */

/**
 * @extends {Component<WishlistRefs>}
 */
class WishlistWidget extends Component {
  constructor() {
    super();
    this.productId = this.dataset.id || '';
    this.productHandle = this.dataset.handle || '';
    this.productTitle = this.dataset.title || '';
    this.isLoading = false;
    this.useServer = Theme.wishlist?.useServer || false;
    this.endpoint = Theme.wishlist?.endpoint || '';
    this.customerId = Theme.wishlist?.customerId || '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
    this.loadState();
  }

  setupEventListeners() {
    document.addEventListener(ThemeEvents.wishlistUpdate, this.handleWishlistUpdate.bind(this));
  }

  /**
   * @param {CustomEvent} event
   */
  handleWishlistUpdate(event) {
    const { wishlist, data } = event.detail;
    const isActive = wishlist.includes(this.productHandle);
    this.updateUI(isActive, data.isLoading);
  }

  /**
   * @param {boolean} isActive
   * @param {boolean} isLoading
   */
  updateUI(isActive, isLoading = false) {
    this.classList.toggle('wishlist__widget--active', isActive);

    if (this.refs.icon) {
      this.refs.icon.classList.toggle('wishlist__icon--active', isActive);
      this.refs.icon.classList.toggle('wishlist__icon--loading', isLoading);
    }
  }

  /**
   * @returns {string[]}
   */
  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem('wishlist') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * @param {string[]} wishlist
   */
  saveWishlist(wishlist) {
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
  }

  loadState() {
    const wishlist = this.getWishlist();
    this.updateUI(wishlist.includes(this.productHandle));
  }

  /**
   * @param {string[]} wishlist
   */
  async updateServer(wishlist) {
    if (!this.useServer || !this.endpoint || !this.customerId) return;

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-MagicFX-Auth': '5836747fcb372c47e1043b962285f52cd1a13c0074c38b2e9dbae77c50c7a603',
        },
        body: JSON.stringify({
          customerid: this.customerId,
          wishlist,
        }),
      });

      if (!response.ok) throw new Error('Server error');

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Wishlist server error:', error);
      throw error;
    }
  }

  async handleToggleWishlist() {
    if (this.isLoading || !this.productHandle) return;

    const wishlist = this.getWishlist();
    const isActive = wishlist.includes(this.productHandle);
    const newWishlist = isActive
      ? wishlist.filter((handle) => handle !== this.productHandle)
      : [...wishlist, this.productHandle];

    this.isLoading = true;
    document.dispatchEvent(new WishlistUpdateEvent(newWishlist, this.id, { isLoading: true }));

    try {
      this.saveWishlist(newWishlist);

      if (this.useServer && this.endpoint && this.customerId) {
        requestIdleCallback(() => {
          this.updateServer(newWishlist).catch(() => {});
        });
      }

      this.showNotification(isActive ? 'remove' : 'added');

      const messages = Theme.wishlist;
      const messageKey = isActive ? 'remove' : 'added';
      const message = messageKey === 'remove' ? messages?.remove : messages?.added;

      document.dispatchEvent(
        new WishlistUpdateEvent(newWishlist, this.id || '', {
          isLoading: false,
          message: message || '',
        }),
      );
    } catch (error) {
      this.saveWishlist(wishlist);
      document.dispatchEvent(
        new WishlistUpdateEvent(wishlist, this.id || '', {
          isLoading: false,
          isError: true,
          message: 'Error updating wishlist',
        }),
      );
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Shows a notification with the given type (debounced to prevent spam)
   * @param {string} type
   */
  showNotification = debounce((type) => {
    const notificationPopover = this.querySelector('[id^="wishlist-notification-"]');
    if (!notificationPopover) return;

    const titleEl = notificationPopover.querySelector('.wishlist-notification__title');
    const messageEl = notificationPopover.querySelector('.wishlist-notification__message');

    if (titleEl) titleEl.textContent = this.productTitle;
    if (messageEl) {
      const messages = Theme.wishlist;
      messageEl.textContent =
        (type === 'add'
          ? messages?.add
          : type === 'remove'
          ? messages?.remove
          : type === 'added'
          ? messages?.added
          : '') || '';
    }

    const anchoredPopover = this.querySelector('anchored-popover-component');
    if (anchoredPopover) {
      const trigger = this.querySelector('[ref="trigger"]');
      if (trigger) {
        setTimeout(() => {
          /** @type {HTMLElement & {showPopover?: () => void}} */
          const popover = /** @type {HTMLElement} */ (notificationPopover);
          if (popover.showPopover) {
            popover.showPopover();

            setTimeout(() => {
              /** @type {HTMLElement & {hidePopover?: () => void}} */
              const hidePopover = /** @type {HTMLElement} */ (notificationPopover);
              if (hidePopover.matches(':popover-open') && hidePopover.hidePopover) {
                hidePopover.hidePopover();
              }
            }, 2000);
          }
        }, 10);
        return;
      }
    }
  }, 300);
}

/**
 * @typedef {Object} WishlistCounterRefs
 * @property {HTMLElement} [background] - The counter background element
 * @property {HTMLElement} [text] - The counter text element
 */

/**
 * @extends {Component<WishlistCounterRefs>}
 */
class WishlistCounter extends Component {
  constructor() {
    super();
    this.count = 0;
    this.showCounter = !this.classList.contains('wishlist-counter--hide-counter');
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
    this.updateCount();
  }

  setupEventListeners() {
    document.addEventListener(ThemeEvents.wishlistUpdate, this.handleWishlistUpdate.bind(this));
  }

  /**
   * @param {CustomEvent} event
   */
  handleWishlistUpdate(event) {
    const { wishlist } = event.detail;
    this.count = wishlist.length;
    this.updateUI();
    this.updateHeaderIcon();
  }

  updateCount() {
    try {
      const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      this.count = wishlist.length;
      this.updateUI();
      this.updateHeaderIcon();
    } catch {
      this.count = 0;
      this.updateUI();
      this.updateHeaderIcon();
    }
  }

  updateUI = debounce(() => {
    if (this.showCounter) {
      this.classList.toggle('wishlist-counter--active', this.count > 0);

      if (this.count > 0) {
        this.classList.add('wishlist-counter--animating');
        setTimeout(() => {
          this.classList.remove('wishlist-counter--animating');
        }, 300);
      }

      const textElement = this.querySelector('.cart-bubble__text');
      if (textElement) {
        textElement.textContent = this.count.toString();
      }
    }
  }, 100);

  updateHeaderIcon() {
    const headerIcon = document.querySelector('.header__icon--wishlist');
    if (headerIcon) {
      headerIcon.classList.toggle('header__icon--wishlist-active', this.count > 0);
    }
  }
}

customElements.define('wishlist-widget', WishlistWidget);
customElements.define('wishlist-counter', WishlistCounter);
